import React, { FC } from "react";

import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import { styled } from "@mui/material";

import {
  ColorError,
  ColorPrimary,
  ColorSecondary,
  ColorSuccess,
  ColorTestPilot,
  ColorWarning,
} from "./theme";

const StyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
}));

const WhiteStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: ColorSecondary.main,
  backgroundColor: "white",
  borderColor: ColorSecondary.light,
  "&:hover, &:focus": {
    color: "white",
    backgroundColor: ColorPrimary.main,
    borderColor: ColorPrimary.light,
  },
}));

const WhiteOutlinedStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: "white",
  backgroundColor: "transparent",
  borderColor: "white",
  "&:hover, &:focus": {
    color: "white",
    backgroundColor: ColorPrimary.main,
    borderColor: ColorPrimary.light,
  },
}));

const WarningStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: "white",
  backgroundColor: ColorWarning.main,
  "&:hover, &:focus": {
    backgroundColor: "#F8A302",
  },
}));

const TestPilotBlueStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: "white",
  backgroundColor: ColorTestPilot.blue,
  "&:hover, &:focus": {
    backgroundColor: ColorTestPilot.hoverBlue,
  },
}));

const TestPilotBlueOutlinedStyledButton = styled(LoadingButton)(
  ({ theme }) => ({
    minHeight: "40px",
    textTransform: "none",
    color: ColorTestPilot.blue,
    backgroundColor: "transparent",
    border: "solid 1px #273053",
    "&:hover, &:focus": {
      color: "white",
      backgroundColor: ColorTestPilot.hoverBlue,
    },
    "&:disabled,": {
      border: "solid 1px #A3A3A3",
    },
  })
);

const TestPilotBlueTextStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: ColorTestPilot.blue,
  backgroundColor: "transparent",
  "&:hover, &:focus": {
    color: ColorTestPilot.hoverBlue,
  },
}));

const ErrorStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: "white",
  backgroundColor: ColorError.main,
  "&:hover, &:focus": {
    backgroundColor: ColorError.dark,
  },
}));

const SuccessStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: "white",
  backgroundColor: ColorSuccess.main,
  "&:hover, &:focus": {
    backgroundColor: "#239723",
  },
}));

const LoadingStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: ColorPrimary.main,
  backgroundColor: "#DBE0EB",
  "&:hover, &:focus": {
    color: ColorPrimary.main,
    backgroundColor: "#DBE0EB",
    cursor: "auto",
  },
}));

const GrayStyledButton = styled(LoadingButton)(({ theme }) => ({
  minHeight: "40px",
  textTransform: "none",
  color: "white",
  backgroundColor: "#BFBFBF",
  "&:hover, &:focus": {
    color: "white",
    backgroundColor: "#A3A3A3",
  },
}));

export interface IgButtonProps extends LoadingButtonProps {
  customColor?: string;
  customBackgroundColor?: string;
  customBorderColor?: string;
  igVariant?:
    | "white"
    | "whiteOutlined"
    | "testPilotBlue"
    | "testPilotBlueOutlined"
    | "testPilotBlueText"
    | "warning"
    | "error"
    | "success"
    | "loading"
    | "gray";
  component?: any;
}

/**
 * @see LoadingButton
 */
export const IgButton: FC<IgButtonProps> = (props) => {
  const {
    children,
    onClick,
    loading,
    variant,
    sx,
    customColor,
    customBackgroundColor,
    igVariant,
    ...rest
  } = props;

  if (igVariant === "white") {
    return (
      // @ts-ignore
      <WhiteStyledButton
        disableElevation
        loading={loading}
        variant={"outlined"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </WhiteStyledButton>
    );
  }

  if (igVariant === "whiteOutlined") {
    return (
      // @ts-ignore
      <WhiteOutlinedStyledButton
        disableElevation
        loading={loading}
        variant={"outlined"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </WhiteOutlinedStyledButton>
    );
  }

  if (igVariant === "warning") {
    return (
      // @ts-ignore
      <WarningStyledButton
        disableElevation
        loading={loading}
        variant={"contained"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </WarningStyledButton>
    );
  }

  if (igVariant === "error") {
    return (
      // @ts-ignore
      <ErrorStyledButton
        disableElevation
        loading={loading}
        variant={"contained"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </ErrorStyledButton>
    );
  }

  if (igVariant === "success") {
    return (
      // @ts-ignore
      <SuccessStyledButton
        disableElevation
        loading={loading}
        variant={"contained"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </SuccessStyledButton>
    );
  }

  if (igVariant === "loading") {
    return (
      // @ts-ignore
      <LoadingStyledButton
        startIcon={<RestartAltOutlinedIcon className={"spin"} />}
        disableElevation
        loading={loading}
        variant={"contained"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={undefined}
      >
        {children}
      </LoadingStyledButton>
    );
  }

  if (igVariant === "gray") {
    return (
      // @ts-ignore
      <GrayStyledButton
        disableElevation
        loading={loading}
        variant={"contained"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </GrayStyledButton>
    );
  }

  if (igVariant === "testPilotBlue") {
    return (
      // @ts-ignore
      <TestPilotBlueStyledButton
        disableElevation
        loading={loading}
        variant={"contained"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </TestPilotBlueStyledButton>
    );
  }

  if (igVariant === "testPilotBlueOutlined") {
    return (
      // @ts-ignore
      <TestPilotBlueOutlinedStyledButton
        disableElevation
        loading={loading}
        variant={"contained"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </TestPilotBlueOutlinedStyledButton>
    );
  }

  if (igVariant === "testPilotBlueText") {
    return (
      // @ts-ignore
      <TestPilotBlueTextStyledButton
        disableElevation
        loading={loading}
        variant={"text"}
        {...rest}
        // @ts-ignore
        sx={{ ...sx }}
        onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
      >
        {children}
      </TestPilotBlueTextStyledButton>
    );
  }

  return (
    // @ts-ignore
    <StyledButton
      disableElevation
      loading={loading}
      variant={variant}
      {...rest}
      sx={
        variant === "contained"
          ? {
              paddingLeft: "25px",
              paddingRight: "25px",
              color: customColor,
              backgroundColor: customBackgroundColor,
              ...sx,
            }
          : {
              color: customColor,
              backgroundColor: customBackgroundColor,
              ...sx,
            }
      }
      onClick={Boolean(loading) && Boolean(onClick) ? undefined : onClick}
    >
      {!loading && children}
    </StyledButton>
  );
};
